import {
	v1_ExternalAccountChooseBank_Clicked,
	v1_ExternalAccountLinkSetup_Closed,
	v1_ExternalAccountLinkSetup_Failed,
	v1_ExternalAccountLinkSetup_Succeeded,
	v1_ExternalAccountLinkSetup_Submitted,
	v1_ExternalAccountLinkSetupResults_Viewed,
} from '@stashinvest/product-events-javascript/lib/v1/event_static_methods';
import {
	v1_ExternalAccountChooseBank_ClickedProperties,
	v1_ExternalAccountLinkSetup_ClosedProperties,
	v1_ExternalAccountLinkSetup_FailedProperties,
	v1_ExternalAccountLinkSetup_SucceededProperties,
	v1_ExternalAccountLinkSetup_SubmittedProperties,
	v1_ExternalAccountLinkSetupResults_ViewedProperties,
} from '@stashinvest/product-events-javascript/lib/v1/properties';
import { Origin } from '@stashinvest/product-events-javascript/lib/enum_properties';

/**
 * Logs analytics when user prematurely exits the Plaid flow,
 * or an error occurs that kicks the user out of the flow.
 * The status indicates the last step the user was on before exiting.
 */
export function trackOnSetupClosed(error, metadata, isOnboarding, track) {
	track(
		v1_ExternalAccountLinkSetup_Closed({
			origin: isOnboarding ? Origin.onboarding : null,
			external_account_type:
				v1_ExternalAccountLinkSetup_ClosedProperties.ExternalAccountType.bank,
			external_account_connection_type:
				v1_ExternalAccountLinkSetup_ClosedProperties.ExternalAccountConnectionType
					.instant,
			external_link_session_id: metadata.link_session_id,
			external_institution_name:
				metadata.institution_name || metadata.institution?.name || null,
			link_status: Object.values(
				v1_ExternalAccountLinkSetup_ClosedProperties.LinkStatus
			).includes(metadata.status)
				? metadata.status
				: v1_ExternalAccountLinkSetup_ClosedProperties.LinkStatus.other,
			error_message: error?.error_code,
		})
	);
}

/**
 * Logs analytics when Plaid onEvent fires throughout the Plaid flow.
 * There are more events that can be tracked, outlined here
 * @see https://plaid.com/docs/link/web/#onevent
 * Event names that were tracked in old segment are as follows:
 * - OPEN
 * - SELECT_INSTITUTION
 * - SUBMIT_CREDENTIALS
 * - SUBMIT_MFA
 * - OPEN_OAUTH
 * - FAIL_OAUTH
 * - TRANSITION_VIEW
 * - SELECT_ACCOUNT
 * - MATCHED_SELECT_INSTITUTION
 */
export function trackOnEvent(eventName, metadata, isOnboarding, track) {
	if (eventName === 'SELECT_INSTITUTION') {
		return track(
			v1_ExternalAccountChooseBank_Clicked({
				origin: isOnboarding ? Origin.onboarding : null,
				external_account_type:
					v1_ExternalAccountChooseBank_ClickedProperties.ExternalAccountType.bank,
				external_account_connection_type:
					v1_ExternalAccountChooseBank_ClickedProperties.ExternalAccountConnectionType
						.instant,
				external_institution_name: metadata.institution_name || null,
				external_link_session_id: metadata.link_session_id,
			})
		);
	}
}

export function trackOnSetupSubmitted(
	isOnboarding,
	track,
	selectedAccountId,
	linkSessionId,
	institutionName
) {
	track(
		v1_ExternalAccountLinkSetup_Submitted({
			origin: isOnboarding ? Origin.onboarding : null,
			external_account_connection_type:
				v1_ExternalAccountLinkSetup_SubmittedProperties.ExternalAccountConnectionType
					.instant,
			external_account_type:
				v1_ExternalAccountLinkSetup_SubmittedProperties?.ExternalAccountType?.bank,
			externallink_id: selectedAccountId,
			external_link_session_id: linkSessionId,
			external_institution_name: institutionName,
		})
	);
}

export function trackOnSetupFailed(isOnboarding, track) {
	track(
		v1_ExternalAccountLinkSetup_Failed({
			origin: isOnboarding ? Origin.onboarding : null,
			external_account_type:
				v1_ExternalAccountLinkSetup_FailedProperties.ExternalAccountType.bank,
			external_account_connection_type:
				v1_ExternalAccountLinkSetup_FailedProperties.ExternalAccountConnectionType
					.instant,
			external_account_provider:
				v1_ExternalAccountLinkSetup_FailedProperties.ExternalAccountProvider.plaid,
			error_type: v1_ExternalAccountLinkSetup_FailedProperties?.ErrorType?.apiError,
		})
	);
}

export function trackOnSetupSucceeded(isOnboarding, track) {
	track(
		v1_ExternalAccountLinkSetup_Succeeded({
			origin: isOnboarding ? Origin.onboarding : null,
			external_account_connection_type:
				v1_ExternalAccountLinkSetup_SucceededProperties.ExternalAccountConnectionType
					.instant,
			external_account_type:
				v1_ExternalAccountLinkSetup_SucceededProperties.ExternalAccountType.bank,
			external_account_provider:
				v1_ExternalAccountLinkSetup_SucceededProperties.ExternalAccountProvider.plaid,
		})
	);
}

export function trackOnSetupResults(isOnboarding, track, result) {
	track(
		v1_ExternalAccountLinkSetupResults_Viewed({
			origin: isOnboarding ? Origin.onboarding : null,
			external_account_type:
				v1_ExternalAccountLinkSetupResults_ViewedProperties.ExternalAccountType.bank,
			external_account_connection_type:
				v1_ExternalAccountLinkSetupResults_ViewedProperties.ExternalAccountConnectionType
					.instant,
			result,
		})
	);
}
